 import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatAPI, FileAPI } from "../api/apiClient";
import { createSignalRConnection } from "../api/signalRClient";
import * as signalR from "@microsoft/signalr";

type Msg = {
  messageNo?: number;
  message: string;
  theUser?: number;
  sender?: string;
  sentAt?: string | number | Date;
  messageDate?: string | number | Date;
  MessageType?: number;
  EditedAt?: string | null;
  IsDeleted?: boolean;
};

type GroupedMessages = {
  dateLabel: string;
  items: Msg[];
};

type UserInfo = {
  userNo: number;
  userName: string;
};

const normalizeText = (value: string | undefined | null) =>
  (value ?? "").trim().replace(/\s+/g, " ");

const normalizeMessage = (m: any, fallbackUser?: UserInfo | null): Msg => ({
  messageNo: m?.messageNo ?? m?.MessageNo,
  message: m?.message ?? m?.Message ?? "",
  theUser:
    m?.theUser ??
    m?.TheUser ??
    m?.Sender?.UserNo ??
    m?.Sender?.userNo ??
    fallbackUser?.userNo,
  sender:
    m?.sender?.userName ??
    m?.Sender?.UserName ??
    m?.Sender?.userName ??
    m?.Sender?.UserName ??
    m?.sender ??
    m?.Sender ??
    m?.userName ??
    m?.UserName ??
    fallbackUser?.userName ??
    "Unknown",
  sentAt:
    m?.sentAt ??
    m?.SentAt ??
    m?.messageDate ??
    m?.MessageDate ??
    new Date().toISOString(),
  messageDate: m?.messageDate ?? m?.MessageDate,
  MessageType: m?.messageType ?? m?.MessageType ?? 1,
  EditedAt: m?.editedAt ?? m?.EditedAt ?? null,
  IsDeleted: m?.isDeleted ?? m?.IsDeleted ?? false,
});

function getDateLabel(dateStr: string | number | Date): string {
  const msgDate = new Date(
    typeof dateStr === "string" && !dateStr.endsWith("Z")
      ? dateStr + "Z"
      : dateStr
  );

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday = msgDate.toDateString() === today.toDateString();
  const isYesterday = msgDate.toDateString() === yesterday.toDateString();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";

  return msgDate.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function groupMessages(messages: Msg[]): GroupedMessages[] {
  const groups: Record<string, Msg[]> = {};

  for (const msg of messages) {
    const label = getDateLabel(msg.sentAt || msg.messageDate || new Date());
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
  }

  return Object.entries(groups).map(([dateLabel, items]) => ({
    dateLabel,
    items,
  }));
}

export default function IssueChat({ taskId }: { taskId: number }) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [editingMsgId, setEditingMsgId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Msg | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) return;

    try {
      const u = JSON.parse(stored);
      setCurrentUser({
        userNo: u.userNo ?? u.UserNo,
        userName: u.userName ?? u.UserName,
      });
    } catch {
      console.error("Invalid user data in localStorage");
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadMessages = async (tId: number) => {
    try {
      const data = await ChatAPI.getIssueChat(tId);
      const list = Array.isArray(data) ? data : [];
      setMessages(list.map((m) => normalizeMessage(m)));
    } catch (err) {
      console.error("Failed to load chat messages:", err);
      setMessages([]);
    }
  };

  useEffect(() => {
    if (!taskId) return;

    let active = true;
    const conn = createSignalRConnection();
    setConnection(conn);

    const start = async () => {
      try {
        await loadMessages(taskId);

        await conn.start();
        if (!active) return;

        await conn.invoke("JoinIssue", taskId);

        conn.on(
          "ReceiveMessage",
          (
            issueId: number,
            messageNo: number,
            userNo: number,
            userName: string,
            text: string,
            sentAt: string
          ) => {
            if (issueId !== taskId) return;

            const incoming = normalizeMessage(
              {
                messageNo,
                message: text,
                theUser: userNo,
                sender: userName,
                sentAt,
                MessageType: 1,
                IsDeleted: false,
              },
              currentUser
            );

            setMessages((prev) => {
              if (
                incoming.messageNo != null &&
                prev.some((m) => m.messageNo === incoming.messageNo)
              ) {
                return prev;
              }
              return [...prev, incoming];
            });
          }
        );

        conn.on(
          "IssueMessageEdited",
          (
            issueId: number,
            messageNo: number,
            newText: string,
            userNo: number,
            userName: string,
            editedAt: string
          ) => {
            if (issueId !== taskId) return;

            setMessages((prev) =>
              prev.map((m) =>
                m.messageNo === messageNo
                  ? { ...m, message: newText, EditedAt: editedAt }
                  : m
              )
            );
          }
        );

        conn.on(
          "IssueMessageDeleted",
          (
            issueId: number,
            messageNo: number,
            userNo: number,
            userName: string
          ) => {
            if (issueId !== taskId) return;

            setMessages((prev) => prev.filter((m) => m.messageNo !== messageNo));
          }
        );
         conn.on(
  "IssueUpdated",
  async (projectId: number, issueId: number) => {
    if (issueId !== taskId) return;

    await loadMessages(taskId);
  }
); 

      } catch (err) {
        console.error("SignalR connection failed:", err);
      }
    };

    start();

    return () => {
      active = false;
      conn.off("ReceiveMessage");
      conn.off("IssueMessageEdited");
      conn.off("IssueMessageDeleted");
      conn.off("IssueUpdated");
      conn.stop();
    };
  }, [taskId]);

  const handleSend = async () => {
    if (!currentUser) return;

    const draftText = text;
    const draftFile = selectedFile;

    try {
      let finalMessage = text.trim();

      if (selectedFile) {
        const uploaded = await FileAPI.upload(
          selectedFile,
          currentUser.userNo,
          taskId
        );

        const fileMessage = `[FILE]|${uploaded.fileUrl}|${uploaded.originalFileName}|${uploaded.fileSizeBytes}`;
        finalMessage = text.trim() ? `${fileMessage}\n${text}` : fileMessage;

        setSelectedFile(null);
      }

      if (!finalMessage.trim()) return;

      setText("");

      const savedRaw = await ChatAPI.postIssueMessage(taskId, finalMessage);
      const saved = normalizeMessage(savedRaw, currentUser);

      setMessages((prev) => {
        if (
          saved.messageNo != null &&
          prev.some((m) => m.messageNo === saved.messageNo)
        ) {
          return prev;
        }
        return [...prev, saved];
      });

      try {
        await connection?.invoke("SendMessage", taskId, finalMessage, currentUser.userNo);
      } catch (signalRErr) {
        console.error("SignalR send failed:", signalRErr);
      }
    } catch (err) {
      console.error("Send failed:", err);
      setText(draftText);
      setSelectedFile(draftFile);
    }
  };

  const saveEdit = async (msg: Msg) => {
    if (!msg.messageNo) return;

    try {
      setMessages((prev) =>
        prev.map((m) =>
          m.messageNo === msg.messageNo
            ? { ...m, message: editText, EditedAt: new Date().toISOString() }
            : m
        )
      );

      await ChatAPI.editMessage(msg.messageNo, editText);

      try {
        await connection?.invoke(
          "EditIssueMessage",
          taskId,
          msg.messageNo,
          editText,
          currentUser?.userNo
        );
      } catch (signalRErr) {
        console.error("SignalR edit failed:", signalRErr);
      }

      setEditingMsgId(null);
      setEditText("");
    } catch (err) {
      console.error("Edit failed", err);
    }
  };

  const handleDelete = async (msg: Msg) => {
    if (!msg.messageNo) return;

    try {
      setMessages((prev) => prev.filter((m) => m.messageNo !== msg.messageNo));
      setDeleteTarget(null);

      await ChatAPI.deleteMessage(msg.messageNo);

      try {
        await connection?.invoke(
          "DeleteIssueMessage",
          taskId,
          msg.messageNo,
          currentUser?.userNo
        );
      } catch (signalRErr) {
        console.error("SignalR delete failed:", signalRErr);
      }
    } catch (err) {
      console.error("Delete failed", err);
      await loadMessages(taskId);
    }
  };

  const grouped = groupMessages(messages);

  return (
    <div className="flex flex-col h-full bg-gray-50 w-full overflow-x-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 bg-gray-100">
        {grouped.map((group, gidx) => (
          <div key={gidx}>
            <div className="text-center text-xs text-gray-500 my-4">
              <div className="inline-block bg-gray-200 px-3 py-1 rounded-full">
                {group.dateLabel}
              </div>
            </div>

            {group.items.map((m, idx) => {
              const isFile = m.message?.startsWith("[FILE]");
              const isSystem = m.MessageType !== undefined && m.MessageType <= 0;

              const isMine =
                !!currentUser &&
                Number(m.theUser) === Number(currentUser.userNo);

              const rawTime = m.sentAt || m.messageDate;

              const when = rawTime
                ? new Date(
                    typeof rawTime === "string" && !rawTime.endsWith("Z")
                      ? rawTime + "Z"
                      : rawTime
                  ).toLocaleTimeString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "";

              const safeTime = rawTime || new Date();
              const messageTime = new Date(
                typeof safeTime === "string" && !safeTime.endsWith("Z")
                  ? safeTime + "Z"
                  : safeTime
              );
              const now = new Date();
              const diffMinutes = (now.getTime() - messageTime.getTime()) / (1000 * 60);
              const canEdit = diffMinutes <= 15;

              if (isSystem) {
                return (
                  <div key={idx} className="flex justify-center my-2">
                    <div className="bg-gray-300 text-gray-700 px-3 py-1 rounded-full text-xs text-center">
                      {m.message}
                    </div>
                  </div>
                );
              }

              const canShowActions =
                isMine && !m.IsDeleted && !isFile && m.messageNo != null;

              return (
                <div
                  key={idx}
                  className={`flex mb-3 ${isMine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`relative max-w-[70%] break-words p-3 rounded-2xl shadow-sm ${
                      isMine
                        ? "bg-blue-600 text-white rounded-br-none"
                        : "bg-white text-gray-800 rounded-bl-none"
                    }`}
                  >
                    {!isMine && (
                      <div className="text-xs font-semibold text-gray-600 mb-1 flex justify-between">
                        <span>{m.sender || "Unknown"}</span>
                        <span className="text-[10px] opacity-70">{when}</span>
                      </div>
                    )}

                    {isMine && (
                      <div className="text-[10px] text-right opacity-75 mb-1">{when}</div>
                    )}

                    {canShowActions && (
                      <div className="absolute top-1 right-2 text-xs">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(menuOpenId === m.messageNo ? null : m.messageNo!);
                          }}
                          className="text-white opacity-70 hover:opacity-100"
                        >
                          ⋮
                        </button>

                        {menuOpenId === m.messageNo && (
                          <div className="absolute right-0 mt-1 bg-white text-black rounded shadow-md z-10">
                            {canEdit && (
                              <button
                                onClick={() => {
                                  setEditingMsgId(m.messageNo!);
                                  setEditText(m.message);
                                  setMenuOpenId(null);
                                }}
                                className="block px-3 py-1 hover:bg-gray-100 w-full text-left"
                              >
                                ✏️ Edit
                              </button>
                            )}

                            <button
                              onClick={() => {
                                setDeleteTarget(m);
                                setMenuOpenId(null);
                              }}
                              className="block px-3 py-1 hover:bg-gray-100 w-full text-left text-red-600"
                            >
                              🗑 Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="text-sm leading-snug">
                      {m.IsDeleted ? (
                        <span className="italic text-gray-400">Message deleted</span>
                      ) : m.message?.startsWith("[FILE]") ? (
                        (() => {
                          const [filePart, ...captionParts] = m.message.split("\n");
                          const parts = filePart.replace("[FILE]|", "").split("|");

                          const fileUrl = parts[0];
                          const fileName = parts[1] || fileUrl.split("/").pop();
                          const fileSize = parts[2];
                          const caption = captionParts.join("\n");

                          const fullUrl =
                            `${process.env.REACT_APP_API_BASE?.replace("/api", "")}${fileUrl}`;

                          const sizeInKB = fileSize
                            ? (parseInt(fileSize) / 1024).toFixed(1)
                            : null;

                          return (
                            <div className="flex flex-col gap-2">
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-3 bg-gray-100 p-3 rounded-xl"
                              >
                                <div className="text-2xl">📎</div>

                                <div className="flex flex-col">
                                  <span className="font-medium text-gray-800 break-all">
                                    {fileName}
                                  </span>

                                  {sizeInKB && (
                                    <span className="text-xs text-gray-500">
                                      {sizeInKB} KB
                                    </span>
                                  )}
                                </div>
                              </a>

                              {caption && <div className="text-sm">{caption}</div>}
                            </div>
                          );
                        })()
                      ) : editingMsgId === m.messageNo ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="border rounded px-2 py-1 text-black"
                            rows={3}
                            autoFocus
                          />

                          <button
                            onClick={() => saveEdit(m)}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded"
                          >
                            Save
                          </button>

                          <button
                            onClick={() => setEditingMsgId(null)}
                            className="text-xs bg-gray-300 px-2 py-1 rounded"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          {m.message}
                          {m.EditedAt && (
                            <span className="text-[10px] opacity-70 ml-2">(edited)</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {selectedFile && (
        <div className="text-xs text-gray-600 ml-12 mt-1 max-w-[200px] truncate">
          📎 {selectedFile.name}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-80">
            <h3 className="text-lg font-semibold mb-3">Delete message</h3>

            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete this message?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={() => handleDelete(deleteTarget)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-t p-3 flex gap-2 items-center z-20 w-full">
        <input
          type="file"
          id="fileUpload"
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setSelectedFile(e.target.files[0]);
              e.target.value = "";
            }
          }}
        />

        <button
          type="button"
          onClick={() => document.getElementById("fileUpload")?.click()}
          className="shrink-0 px-3 py-2 bg-gray-200 rounded-full hover:bg-gray-300"
        >
          📎
        </button>

        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={selectedFile ? selectedFile.name : "Type a message..."}
          className="flex-1 min-w-0 border border-gray-300 rounded-full px-4 py-2"
        />

        <button
          onClick={handleSend}
          disabled={!text.trim() && !selectedFile}
          className={`px-5 py-2 rounded-full transition ${
            text.trim() || selectedFile
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Send
        </button>
      </div>
    </div>
  );
} 
